import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { PriceSummary } from "../components/PriceSummary";
import api, { apiError, getDraft, clearDraft, AED } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, CreditCard, Loader2, ShieldCheck, Truck } from "lucide-react";

const COUNTRY_CODES = ["+971", "+966", "+974", "+973", "+968", "+965", "+44", "+1", "+91", "+92", "+63", "+20", "+7", "+86", "+49", "+33"];

export default function Payment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const draft = useMemo(() => getDraft(), []);
  const [driver, setDriver] = useState({ name: "", email: "", phone: "", license_no: "" });
  const [countryCode, setCountryCode] = useState("+971");
  const [card, setCard] = useState({ number: "", expiry: "", cvv: "", holder: "" });
  const [address, setAddress] = useState("");
  const [checks, setChecks] = useState({ age: false, terms: false, deposit: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!draft?.selections || !draft?.quote) {
      navigate("/");
      return;
    }
    if (user === null) navigate("/login?next=/payment");
    if (user) setDriver((d) => ({ ...d, name: d.name || user.name || "", email: d.email || user.email || "", phone: d.phone || (user.phone || "").replace(/^\+\d{1,3}\s?/, ""), license_no: d.license_no || user.license_no || "" }));
  }, [draft, user, navigate]);

  if (!draft?.selections || !user) return null;
  const payNow = draft.selections.pay_type === "now";
  const isDelivery = !!draft.selections.delivery?.enabled;
  const isPremium = draft.vehicle.insurance_tier === "premium";
  const ndw = draft.selections.ndw;
  const cap = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const formatCard = (v) => v.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
  const formatExpiry = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 4);
    return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!driver.name.trim() || !driver.email.trim() || !driver.phone.trim()) {
      toast.error(t("Please complete the driver details"));
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(driver.email.trim())) {
      toast.error(t("That email doesn't look complete"));
      return;
    }
    if (isDelivery && !address.trim()) {
      toast.error(t("Please enter your delivery address"));
      return;
    }
    if (!checks.age || !checks.terms || (!ndw && !checks.deposit)) {
      toast.error(t("Please confirm the required declarations to continue"));
      return;
    }
    if (payNow) {
      const digits = card.number.replace(/\s/g, "");
      if (digits.length < 15 || card.expiry.length < 5 || card.cvv.length < 3) {
        toast.error(t("Please enter valid card details (demo, any numbers work)"));
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...draft.selections,
        delivery: isDelivery ? { ...draft.selections.delivery, address: address.trim() } : null,
        flight_number: draft.flight?.number || null,
        flight_arrival: draft.flight?.arrival || null,
        driver: { ...driver, phone: `${countryCode} ${driver.phone.trim()}` },
        payment: payNow ? { card_last4: card.number.replace(/\s/g, "").slice(-4), card_holder: card.holder } : null,
      };
      const { data } = await api.post("/bookings", payload);
      clearDraft();
      toast.success(t("Booking confirmed!"));
      navigate(`/confirmation/${data.booking_ref}`);
    } catch (err) {
      toast.error(apiError(err));
      if (err?.response?.status === 409) navigate(-1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full flex-1 grid lg:grid-cols-[1fr_360px] gap-8">
        <form onSubmit={submit} className="space-y-6">
          <div className="bg-[#FFDC00]/20 border border-[#FFDC00] rounded-lg px-4 py-3 text-sm text-[#0F1E17] flex items-center gap-2" data-testid="demo-mode-banner">
            <ShieldCheck className="w-4 h-4 text-[#009900]" />
            <span><strong>{t("You're in demo mode")}</strong>{t(". This is a safe run-through, so no real payment is taken. Secure card payments are coming soon.")}</span>
          </div>

          <div className="bg-white rounded-lg border border-[#e2ebe4] p-5">
            <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-4">{t("Main driver details")}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label">{t("Full name (as on license) *")}</label>
                <input className="field-input" value={driver.name} onChange={(e) => setDriver({ ...driver, name: e.target.value })} data-testid="driver-name-input" />
              </div>
              <div>
                <label className="field-label">{t("Email *")}</label>
                <input type="email" className="field-input" value={driver.email} onChange={(e) => setDriver({ ...driver, email: e.target.value })} data-testid="driver-email-input" />
              </div>
              <div>
                <label className="field-label">{t("Phone *")}</label>
                <div className="flex gap-2">
                  <select className="field-input !w-24" value={countryCode} onChange={(e) => setCountryCode(e.target.value)} data-testid="phone-country-select">
                    {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input className="field-input" placeholder="5X XXX XXXX" value={driver.phone} onChange={(e) => setDriver({ ...driver, phone: e.target.value })} data-testid="driver-phone-input" />
                </div>
              </div>
              <div>
                <label className="field-label">{t("Driving license no. (optional)")}</label>
                <input className="field-input" value={driver.license_no} onChange={(e) => setDriver({ ...driver, license_no: e.target.value })} data-testid="driver-license-input" />
              </div>
            </div>
          </div>

          {isDelivery && (
            <div className="bg-white rounded-lg border border-[#e2ebe4] p-5" data-testid="delivery-address-section">
              <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> {t("Delivery address")}</h3>
              <label className="field-label">{t("Building, street & area in {{place}} *", { place: cap(draft.selections.delivery.emirate) })}</label>
              <textarea rows={2} className="field-input" placeholder={t("e.g. Marina Tower 3, Al Marsa Street, Dubai Marina")} value={address}
                onChange={(e) => setAddress(e.target.value)} data-testid="delivery-address-input" />
            </div>
          )}

          {payNow ? (
            <div className="bg-white rounded-lg border border-[#e2ebe4] p-5">
              <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-4 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> {t("Card payment (simulated)")}
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="field-label">{t("Card number *")}</label>
                  <input className="field-input font-mono" placeholder="4242 4242 4242 4242" value={card.number}
                    onChange={(e) => setCard({ ...card, number: formatCard(e.target.value) })} data-testid="payment-card-number-input" />
                </div>
                <div>
                  <label className="field-label">{t("Expiry *")}</label>
                  <input className="field-input font-mono" placeholder="MM/YY" value={card.expiry}
                    onChange={(e) => setCard({ ...card, expiry: formatExpiry(e.target.value) })} data-testid="payment-expiry-input" />
                </div>
                <div>
                  <label className="field-label">{t("CVV *")}</label>
                  <input className="field-input font-mono" placeholder="123" maxLength={4} value={card.cvv}
                    onChange={(e) => setCard({ ...card, cvv: e.target.value.replace(/\D/g, "") })} data-testid="payment-cvv-input" />
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label">{t("Cardholder name")}</label>
                  <input className="field-input" value={card.holder} onChange={(e) => setCard({ ...card, holder: e.target.value })} data-testid="payment-holder-input" />
                </div>
              </div>
              <p className="text-xs text-[#57685e] mt-3 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> {t("Nothing here is stored or charged, it's a safe simulation. Real, bank-grade secure payments are on the way.")}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-[#e2ebe4] p-5" data-testid="pay-later-notice">
              <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-2">{t("Pay at counter")}</h3>
              <p className="text-sm text-[#57685e]">{t("You've chosen to pay when you collect the car. The total shown is the standard rate, with nothing to pay online now.")}</p>
            </div>
          )}

          {/* Good to know before handover */}
          <div className="bg-white rounded-lg border border-[#e2ebe4] p-5" data-testid="good-to-know-panel">
            <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> {t("Good to know before handover")}</h3>
            <ul className="text-xs text-[#57685e] space-y-1.5 list-disc pl-4">
              {ndw
                ? <li>{t("No deposit and no security block, waived by your No-Deposit Waiver.")}</li>
                : <li>{t("Security at handover: on a credit card it's a temporary hold (your slip shows \"AUTH\", nothing is charged, released in 21 to 28 days); on a debit card it's a refundable deposit. Bring the physical card in the main driver's name.")}</li>}
              <li>{t("Insurance: comprehensive CDW and third-party cover over 1 million USD are included. Your excess (AED 1,500 standard / AED 2,500 premium) drops to zero with Super CDW, and when a police report confirms you were not at fault.")}</li>
              <li>{t("Fuel: return the car at the same fuel level, or we refill it and charge the fuel plus a 15% admin fee.")}</li>
              <li>{t("Tolls & fines: billed after your rental. Tolls (Salik in Dubai, Darb in Abu Dhabi) add a 25% admin fee, and traffic fines add a 10% admin fee.")}</li>
              <li>{t("Our vehicles are strictly non-smoking (including vapes), and a deep-cleaning charge applies if a car is returned smelling of smoke.")}</li>
              <li>{t("24/7 roadside assistance on 800 EUROPCAR is included on every rental. We no longer fit GPS units, so use Apple CarPlay or Android Auto for navigation.")}</li>
              <li>{t("Abu Dhabi & Al Ain: we don't pick up or deliver there, but you can return at Abu Dhabi Airport or arrange collection for AED 275 + VAT.")}</li>
            </ul>
          </div>

          {/* Confirmation checkboxes */}
          <div className="bg-white rounded-lg border border-[#e2ebe4] p-5 space-y-3" data-testid="confirmation-checkboxes">
            <h3 className="heading-upper text-sm tracking-widest text-[#144733]">{t("Your declarations")}</h3>
            <label className="flex items-start gap-2 text-xs text-[#0F1E17] cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={checks.age} onChange={(e) => setChecks({ ...checks, age: e.target.checked })} data-testid="check-age-license" />
              <span>{t("I am 21 years of age{{premium}} and legally allowed to drive in the UAE. I understand I must present a physical copy of my ID/Passport and Driver's License as per UAE regulations.", { premium: isPremium ? t(" (25, as a premium vehicle is selected)") : "" })}</span>
            </label>
            <label className="flex items-start gap-2 text-xs text-[#0F1E17] cursor-pointer">
              <input type="checkbox" className="mt-0.5" checked={checks.terms} onChange={(e) => setChecks({ ...checks, terms: e.target.checked })} data-testid="check-terms" />
              <span>{t("I have reviewed the terms and conditions and agree to them.")}</span>
            </label>
            {!ndw && (
              <label className="flex items-start gap-2 text-xs text-[#0F1E17] cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={checks.deposit} onChange={(e) => setChecks({ ...checks, deposit: e.target.checked })} data-testid="check-deposit" />
                <span>{t("I understand the credit-card pre-authorization or debit-card deposit is processed at vehicle handover and I will have the physical card with me (Apple Pay / Google Pay cannot be accepted per regulations).")}</span>
              </label>
            )}
          </div>

          <button type="submit" disabled={submitting} className="btn-cta rounded w-full py-4 text-sm flex items-center justify-center gap-2" data-testid="submit-payment-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {payNow ? t("Pay {{amount}} & confirm", { amount: AED(draft.quote.total) }) : t("Confirm booking, pay at counter")}
          </button>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[11px] text-[#57685e]" data-testid="payment-reassurance">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#009900]" /> {t("Comprehensive cover included")}</span>
            <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#009900]" /> {t("800 EUROPCAR, 24/7 human support")}</span>
          </div>
        </form>

        <aside className="hidden lg:block">
          <div className="sticky top-20 space-y-4">
            <div className="bg-white rounded-lg border border-[#e2ebe4] p-4 flex items-center gap-3">
              <img src={draft.vehicle.image} alt={draft.vehicle.name} className="w-20 h-14 object-cover rounded" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#57685e]">{draft.vehicle.category}</div>
                <div className="font-bold text-sm text-[#0F1E17]">{draft.vehicle.name} <span className="font-normal text-xs text-[#57685e]">{t("or similar")}</span></div>
              </div>
            </div>
            <PriceSummary quote={draft.quote} testIdPrefix="payment-summary" />
          </div>
        </aside>
      </main>
      <Footer />
    </div>
  );
}
